import { useCallback, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { FolderClient } from 'api/clients/FolderClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { handbookLink } from 'components/Handbook2_0/util'
import { useFolder } from 'hooks/queries/folders/useFolder'
import { useWorkspaces } from 'hooks/useWorkspaces'
import { useRootStore } from 'stores/RootStore'
import { AlmanacFile, isAlmanacFile } from 'types'
import { reportError } from 'utils/error'

export const useCreateFolder = ({
  title,
  emoji,
  chosenParentFolder,
  chosenWorkspaceId,
  handleClose,
  handleError,
  defaultView,
  handbookMode = false,
  databaseMode = false,
}: {
  title: string
  emoji?: string
  chosenWorkspaceId: number
  chosenParentFolder?: AlmanacFile | null
  handleClose: () => void
  handleError?: () => void
  defaultView: string
  handbookMode: boolean
  databaseMode: boolean
}) => {
  const [isCreating, setIsCreating] = useState(false)
  const history = useHistory()
  const workspaces = useWorkspaces()
  const findWorkspace = useCallback((id: number | undefined) => workspaces.find((ws) => ws.id === id), [workspaces])
  const { handbooks: handbooksStore } = useRootStore()

  // We don't have project_id on the AncestorProject, we have to fetch the folder
  const loadedFolder = useFolder(chosenParentFolder?.id, {})
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line no-nested-ternary
  const chosenFolder = isAlmanacFile(chosenParentFolder)
    ? chosenParentFolder
    : loadedFolder.isSuccess
    ? loadedFolder.data?.folder
    : undefined
  const chosenHandbook = chosenFolder?.handbook ?? chosenParentFolder?.handbook

  const createFolder = useCallback(async () => {
    setIsCreating(true)
    const chosenWorkspace = findWorkspace(chosenWorkspaceId)

    if (!chosenWorkspace) return

    const res = await FolderClient.createFolder({
      title,
      emoji,
      workspaceKey: chosenWorkspace.key,
      parentKey: chosenParentFolder?.id,
      defaultView: defaultView,
      handbookMode: handbookMode,
      databaseMode: databaseMode,
    })

    setIsCreating(false)
    if (res?.type === SUCCESS_RESPONSE_TYPE) {
      const folderUrl = res.data.folder
      const {
        data: { folder },
      } = res
      if (folder) {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const path = handbookLink(folder.handbook!, undefined, true)

        if (folderUrl) {
          history.push(path)
        }
      }

      handleClose()
      handbooksStore.refresh(chosenHandbook?.key)
    } else {
      const returnedError = res.data.errors?.[0]

      if (returnedError?.detail === 'has already been taken') {
        showErrorMessageToast(`Folder named ${title} already exists`)
      } else if (!!handleError) {
        handleError()
      } else {
        showErrorMessageToast('Something went wrong')
      }
      reportError(new Error('Error while creating a new folder'), { metaData: { res: res.data.errors } })
    }
  }, [
    title,
    emoji,
    chosenParentFolder?.id,
    chosenHandbook?.key,
    chosenWorkspaceId,
    findWorkspace,
    handleClose,
    history,
    handbooksStore,
    defaultView,
    handbookMode,
    databaseMode,
    handleError,
  ])

  return { createFolder, isCreating }
}
